import classNames from 'classnames';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';

import Icon from 'components/icons/Icon';
import { IconSymbol } from 'components/icons/Icon.types';
import { WrapIf } from 'helpers/HelperComponents';

import './LoadingSpinner.scss';

type LoadingSpinnerProps = {
  /** If true, the loader is visible and animates */
  active: boolean;
  /** If true, wraps the loader in a relatively positioned div */
  wrap?: boolean;
  /** Called when the loader should expire (optional) */
  onExpire?: () => void;
  /** How long (ms) before the loader expires (optional) */
  expiryMs?: number;
  /** Icon size in px */
  size?: number;
  onDark?: boolean;
};

export default function LoadingSpinner(
  props: LoadingSpinnerProps & PropsWithChildren,
) {
  const {
    active,
    wrap,
    expiryMs,
    onExpire,
    size = 32,
    onDark = false,
    children,
  } = props;
  const loaderRef = useRef<HTMLDivElement>(null);
  const [isActive, setIsActive] = useState(active);
  const [angle, setAngle] = useState(0);

  useEffect(() => {
    if (!isActive) return;
    setAngle(0);
    const interval = setInterval(() => {
      setAngle((prev) => {
        const next = (prev + 360 / 16) % 360;
        return next;
      });
    }, 100);
    let timeout: NodeJS.Timeout | undefined;
    if (expiryMs) {
      timeout = setTimeout(() => {
        setIsActive(false);
        onExpire && onExpire();
      }, expiryMs);
    }
    return () => {
      clearInterval(interval);
      if (timeout) clearTimeout(timeout);
    };
  }, [isActive, expiryMs, onExpire]);

  useEffect(() => {
    if (loaderRef.current) {
      loaderRef.current.style.transform = `rotate(${angle}deg)`;
    }
  }, [angle]);

  if (!isActive) return null;

  return (
    <WrapIf
      condition={wrap ?? false}
      wrapper={<div className={classNames('loading_spinner_wrapper')}></div>}
    >
      <>
        <div className="loading_spinner">
          <Icon
            ref={loaderRef}
            symbol={
              onDark ? IconSymbol.spinnerOnDark : IconSymbol.spinnerOnLight
            }
            size={size}
          />
          <div>{children}</div>
        </div>
      </>
    </WrapIf>
  );
}
